import { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, setDoc, serverTimestamp, limit, where, getDocs, arrayUnion } from 'firebase/firestore';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebase';
import './AdminPanel.css';

function AdminPanel({ user, userData }) {
  const [activeSection, setActiveSection] = useState('users');
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activityLogs, setActivityLogs] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'staff'
  });

  useEffect(() => {
    // Fetch users
    const usersQuery = query(collection(db, 'users'));
    const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
      setFilteredUsers(usersData);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Filter users based on search query
    if (searchQuery.trim() === '') {
      setFilteredUsers(users);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = users.filter(u => 
        u.name?.toLowerCase().includes(query) || 
        u.email?.toLowerCase().includes(query) ||
        u.role?.toLowerCase().includes(query)
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, users]);

  useEffect(() => {
    // Fetch activity logs
    const logsQuery = query(
      collection(db, 'activityLogs'),
      orderBy('timestamp', 'desc'),
      limit(50)
    );
    const unsubscribe = onSnapshot(logsQuery, (snapshot) => {
      const logsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setActivityLogs(logsData);
    });

    return () => unsubscribe();
  }, []);

  const ensureGeneralGroupExists = async () => {
    try {
      // Check if General group exists
      const groupsRef = collection(db, 'groups');
      const q = query(groupsRef, where('name', '==', 'General'));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        // Create General group if it doesn't exist
        const generalGroup = {
          name: 'General',
          createdBy: 'system',
          createdAt: serverTimestamp(),
          members: [],
          admins: [],
          isPinned: true,
          isDefault: true
        };
        
        const docRef = await addDoc(groupsRef, generalGroup);
        return docRef.id;
      } else {
        return querySnapshot.docs[0].id;
      }
    } catch (error) {
      console.error('Error ensuring General group exists:', error);
      return null;
    }
  };

  const addUserToGeneralGroup = async (userId) => {
    try {
      const generalGroupId = await ensureGeneralGroupExists();
      
      if (generalGroupId) {
        const groupRef = doc(db, 'groups', generalGroupId);
        await updateDoc(groupRef, {
          members: arrayUnion(userId)
        });
      }
    } catch (error) {
      console.error('Error adding user to General group:', error);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Store admin's current email to sign back in
    const adminEmail = user.email;
    
    // Ask admin for their password to sign back in after creating user
    const adminPassword = prompt('Enter your password to confirm user creation:');
    if (!adminPassword) {
      setLoading(false);
      return;
    }

    try {
      // Create user in Firebase Auth (this will sign in as the new user)
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const newUserId = userCredential.user.uid;

      // Generate unique random 5-digit employee ID
      let employeeId;
      let isUnique = false;
      const usersRef = collection(db, 'users');

      while (!isUnique) {
        employeeId = Math.floor(10000 + Math.random() * 90000).toString();
        const q = query(usersRef, where('employeeId', '==', employeeId));
        const querySnapshot = await getDocs(q);
        if (querySnapshot.empty) {
          isUnique = true;
        }
      }

      // Add user to Firestore with the same UID
      await setDoc(doc(db, 'users', newUserId), {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        employeeId: employeeId,
        createdAt: new Date().toISOString(),
        createdBy: user.uid
      });

      // Add user to General group
      await addUserToGeneralGroup(newUserId);

      // Sign back in as admin
      await signInWithEmailAndPassword(auth, adminEmail, adminPassword);

      // Log activity
      await addDoc(collection(db, 'activityLogs'), {
        userId: user.uid,
        userName: userData?.name || user.email,
        action: 'created_user',
        details: `Created user: ${formData.name} (${formData.email})`,
        timestamp: serverTimestamp()
      });

      setFormData({ name: '', email: '', password: '', role: 'staff' });
      setShowAddUser(false);
      alert('User created successfully and added to General group!');
    } catch (error) {
      console.error('Error creating user:', error);
      if (error.code === 'auth/wrong-password') {
        alert('Incorrect admin password. User may have been created but you were logged out.');
      } else {
        alert(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    setLoading(true);
    try {
      const updateData = {
        name: formData.name.trim()
      };

      // Only update role if it's a valid value
      if (formData.role && ['admin', 'staff'].includes(formData.role)) {
        updateData.role = formData.role;
      }

      await updateDoc(doc(db, 'users', editingUser.id), updateData);

      // Log activity
      await addDoc(collection(db, 'activityLogs'), {
        userId: user.uid,
        userName: userData?.name || user.email,
        action: 'updated_user',
        details: `Updated user: ${formData.name}`,
        timestamp: serverTimestamp()
      });

      setEditingUser(null);
      setFormData({ name: '', email: '', password: '', role: 'staff' });
      alert('User updated successfully!');
    } catch (error) {
      console.error('Error updating user:', error);
      if (error.code === 'permission-denied') {
        alert('Permission denied! Please deploy Firestore rules:\n\nRun: firebase deploy --only firestore:rules\n\nSee DEPLOY_RULES.md for details.');
      } else {
        alert('Failed to update user: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}?\n\nThis will:\n- Remove user from database\n- Remove from all groups\n- Delete their todos\n- Delete their messages\n\nNote: They can still sign in with their email/password. To fully delete, also remove from Firebase Authentication Console.`)) return;

    try {
      setLoading(true);

      // 1. Remove user from all groups
      const groupsQuery = query(collection(db, 'groups'));
      const groupsSnapshot = await getDocs(groupsQuery);
      
      for (const groupDoc of groupsSnapshot.docs) {
        const groupData = groupDoc.data();
        if (groupData.members?.includes(userId)) {
          await updateDoc(doc(db, 'groups', groupDoc.id), {
            members: groupData.members.filter(id => id !== userId),
            admins: (groupData.admins || []).filter(id => id !== userId)
          });
        }
      }

      // 2. Delete user's todos
      const todosQuery = query(collection(db, 'todos'), where('assignedTo', '==', userId));
      const todosSnapshot = await getDocs(todosQuery);
      
      for (const todoDoc of todosSnapshot.docs) {
        await deleteDoc(doc(db, 'todos', todoDoc.id));
      }

      // 3. Delete user's messages
      const messagesQuery = query(collection(db, 'messages'), where('userId', '==', userId));
      const messagesSnapshot = await getDocs(messagesQuery);
      
      for (const messageDoc of messagesSnapshot.docs) {
        await deleteDoc(doc(db, 'messages', messageDoc.id));
      }

      // 4. Delete user document
      await deleteDoc(doc(db, 'users', userId));

      // 5. Log activity
      await addDoc(collection(db, 'activityLogs'), {
        userId: user.uid,
        userName: userData?.name || user.email,
        action: 'deleted_user',
        details: `Completely deleted user: ${userName} (removed from groups, deleted todos and messages)`,
        timestamp: serverTimestamp()
      });

      alert('User completely deleted from database!\n\nRemoved from:\n✓ User database\n✓ All groups\n✓ Todos\n✓ Messages\n\nReminder: To fully prevent login, delete from Firebase Authentication Console:\n1. Go to Firebase Console\n2. Navigate to Authentication > Users\n3. Find and delete the user');
    } catch (error) {
      console.error('Error deleting user:', error);
      if (error.code === 'permission-denied') {
        alert('Permission denied! Please deploy Firestore rules:\n\nRun: firebase deploy --only firestore:rules\n\nSee DEPLOY_RULES.md for details.');
      } else {
        alert('Failed to delete user: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '',
      role: user.role || 'staff' // Default to 'staff' if role is undefined
    });
    setShowAddUser(false);
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setFormData({ name: '', email: '', password: '', role: 'staff' });
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          Admin Panel
        </h1>
      </div>

      <div className="admin-tabs">
        <button
          className={`tab-btn ${activeSection === 'users' ? 'active' : ''}`}
          onClick={() => setActiveSection('users')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          Users ({users.length})
        </button>
        <button
          className={`tab-btn ${activeSection === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveSection('logs')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          Logs
        </button>
      </div>

      {activeSection === 'users' && (
        <div className="users-section">
          <div className="section-header">
            <h2>User Management</h2>
            {!showAddUser && !editingUser && (
              <button onClick={() => setShowAddUser(true)} className="add-user-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add User
              </button>
            )}
          </div>

          {(showAddUser || editingUser) && (
            <div className="user-form-card">
              <h3>{editingUser ? 'Edit User' : 'Add New User'}</h3>
              <form onSubmit={editingUser ? handleUpdateUser : handleAddUser}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      disabled={!!editingUser}
                      required
                    />
                  </div>
                </div>

                {!editingUser && (
                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      minLength="6"
                      required
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div className="form-actions">
                  <button type="submit" className="submit-btn" disabled={loading}>
                    {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddUser(false);
                      cancelEdit();
                    }}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="search-section">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search users by name, email, or role..."
              className="search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="clear-search-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          <div className="users-grid">
            {filteredUsers.map((u) => (
              <div key={u.id} className="user-card">
                <div className="user-avatar">
                  {u.name?.charAt(0).toUpperCase() || u.email.charAt(0).toUpperCase()}
                </div>
                <div className="user-info">
                  <h3>{u.name}</h3>
                  <p className="user-email">{u.email}</p>
                  {u.employeeId && <p className="user-employee-id">ID: {u.employeeId}</p>}
                  <span className={`role-badge role-${u.role || 'staff'}`}>
                    {(u.role || 'staff').toUpperCase()}
                  </span>
                </div>
                {u.id !== user.uid && (
                  <div className="user-actions">
                    <button onClick={() => startEdit(u)} className="edit-btn" title="Edit">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    <button onClick={() => handleDeleteUser(u.id, u.name)} className="delete-btn" title="Delete">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeSection === 'logs' && (
        <div className="logs-section">
          <h2>Activity Logs</h2>
          <div className="logs-list">
            {activityLogs.map((log) => (
              <div key={log.id} className={`log-item log-${log.action}`}>
                <div className="log-icon">
                  {log.action === 'login' && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                  )}
                  {log.action === 'logout' && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  )}
                  {(log.action === 'created_user' || log.action === 'updated_user' || log.action === 'deleted_user') && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  )}
                </div>
                <div className="log-content">
                  <div className="log-header">
                    <span className="log-user">{log.userName}</span>
                    <span className="log-action">{log.action.replace('_', ' ')}</span>
                  </div>
                  {log.details && <p className="log-details">{log.details}</p>}
                  <span className="log-time">
                    {log.timestamp?.toDate().toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
